/* Crumb & Crust theme JS — no dependencies */
(function () {
  'use strict';

  var toastTimer = null;
  var openCartDrawer = null;

  // /products/<handle> and /cart open their overlay on load. Arriving by Back or Forward must
  // not reopen it: closing a product panel goes back in history, and when the previous entry was
  // /cart that used to pop the cart drawer open again.
  function arrivedByHistory() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      return !!(nav && nav.type === 'back_forward');
    } catch (e) { return false; }
  }

  // After closing an overlay that opened on load, when there is nowhere on this site to go back
  // to, swap the address for the shop page so /cart or /products/<handle> is not left in history
  // to reopen the overlay later.
  function leaveAutoAddress() {
    try {
      var root = (window.themeSettings && window.themeSettings.rootUrl) || '/';
      window.history.replaceState(window.history.state, '', root);
    } catch (e) { /* noop */ }
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function showToast(message) {
    var toast = document.getElementById('CartToast');
    if (!toast) return;
    toast.querySelector('.cart-toast__text').textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.hidden = true; }, 4000);
  }

  // Every place the cart appears in the chrome: the badge digits, the "N items" label and the
  // running total in the header pill. Called from both the fetch below and the drawer render
  // so the two can never disagree.
  function syncCartChrome(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.classList.toggle('is-empty', cart.item_count === 0);
    });
    document.querySelectorAll('[data-cart-count-label]').forEach(function (el) {
      el.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    });
    document.querySelectorAll('[data-cart-total]').forEach(function (el) {
      el.textContent = formatMoney(cart.total_price, true);
    });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(syncCartChrome)
      .catch(function () {});
  }

  function addedFeedback(item) {
    showToast('✓ Added to your box');
    updateCartCount();
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.classList.remove('cart-bump');
      void el.offsetWidth;
      el.classList.add('cart-bump');
    });
  }

  // Line-item properties come from inputs named properties[Key]. Only checked radios and
  // non-empty values are sent, so an untouched panel adds nothing to the line (AC-17).
  function collectProperties(form) {
    var properties = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
      var value = el.value.trim();
      if (!value) return;
      properties[el.name.slice(11, -1)] = value;
    });
    return properties;
  }

  function addToCart(variantId, quantity, properties) {
    var line = { id: Number(variantId), quantity: quantity || 1 };
    if (properties && Object.keys(properties).length) line.properties = properties;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(line)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) {
        var err = new Error(e.description || 'Could not add to cart');
        err.isApiError = true;
        throw err;
      });
      return r.json();
    });
  }

  /* ---------- Mobile menu ---------- */
  function initMenu() {
    var toggle = document.querySelector('[data-menu-toggle]');
    var drawer = document.querySelector('[data-menu-drawer]');
    if (!toggle || !drawer) return;
    toggle.addEventListener('click', function () {
      var open = drawer.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Touch devices at desktop widths: hover cannot open the submenu, so the
    // first tap on a parent item opens it and the second tap follows the link.
    var touchOnly = window.matchMedia('(hover: none) and (pointer: coarse)');
    document.addEventListener('click', function (e) {
      if (!touchOnly.matches || window.innerWidth < 900) return;
      var link = e.target.closest('.site-header__item.has-children > .site-header__link');
      if (!link) return;
      var item = link.parentElement;
      if (!item.classList.contains('is-open')) {
        e.preventDefault();
        document.querySelectorAll('.site-header__item.is-open').forEach(function (el) { el.classList.remove('is-open'); });
        item.classList.add('is-open');
      }
    });
  }

  /* ---------- Quantity steppers ---------- */
  function initQty() {
    document.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-qty-minus]');
      var plus = e.target.closest('[data-qty-plus]');
      if (!minus && !plus) return;
      var wrap = (minus || plus).closest('.qty');
      var input = wrap && wrap.querySelector('input');
      if (!input) return;
      var v = parseInt(input.value, 10) || 1;
      var min = parseInt(input.min, 10);
      if (isNaN(min)) min = 1;
      input.value = plus ? v + 1 : Math.max(min, v - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Quick add on product cards ---------- */
  function initQuickAdd() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-quick-add]');
      if (!form) return;
      e.preventDefault();
      var button = form.querySelector('button[type="submit"]');
      var original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      addToCart(form.querySelector('[name="id"]').value, 1)
        .then(addedFeedback)
        .catch(function (err) {
          if (err && err.isApiError) { showToast(err.message); }
          else if (!document.hidden) { form.submit(); }
        })
        .finally(function () {
          button.disabled = false;
          button.textContent = original;
        });
    });
  }

  /* ---------- Product form: options, personalisation, add to cart ---------- */
  // Wires everything inside `root`: the gallery, the option radios, the "Make it yours"
  // panel, add-to-cart and buy-now. The product page and the quick-view panel both call
  // this against the same markup, so the two can never drift apart. onAdded, when given,
  // replaces the default post-add feedback.
  function bindProductForm(root, onAdded) {
    var added = onAdded || addedFeedback;
    // Gallery
    var mainImage = root.querySelector('[data-main-image]');
    root.querySelectorAll('[data-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (!mainImage) return;
        mainImage.src = thumb.dataset.full;
        mainImage.removeAttribute('srcset');
        root.querySelectorAll('[data-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });

    // Option selection → variant resolution
    var form = root.querySelector('[data-product-form]');
    var idInput = root.querySelector('[data-variant-id]');
    var priceWrap = root.querySelector('[data-price-wrapper]');
    var addButton = root.querySelector('[data-add-button]');
    var addText = root.querySelector('[data-add-button-text]');
    var priceMirrors = root.querySelectorAll('[data-price-mirror]');

    var variantJsonEl = root.querySelector('[data-variant-json]');
    var variantData = null;
    if (variantJsonEl) {
      try { variantData = JSON.parse(variantJsonEl.textContent); } catch (err) { variantData = null; }
    }

    function selectedOptionValues() {
      var values = [];
      root.querySelectorAll('[data-option-input]').forEach(function (el) {
        if (el.checked) values[Number(el.dataset.optionIndex)] = el.value;
      });
      return values;
    }

    function findVariant(values) {
      if (!variantData) return null;
      for (var i = 0; i < variantData.length; i++) {
        var v = variantData[i];
        var match = v.options.length === values.length;
        for (var j = 0; match && j < v.options.length; j++) {
          if (v.options[j] !== values[j]) match = false;
        }
        if (match) return v;
      }
      return null;
    }

    // Assigned once the builder is wired below; returns whether the box is exactly full.
    var refreshBuilder = null;

    // A Party Pack cannot be added half-filled, so the builder gets a veto over the button.
    function applyBuilderGate(available) {
      if (!refreshBuilder) return;
      var full = refreshBuilder();
      if (addButton) addButton.disabled = !available || !full;
      if (addText) addText.textContent = !available ? 'Sold out' : (full ? 'Add to box' : 'Fill your box to add');
    }

    function applyVariant(v) {
      var available = !!(v && v.available);
      if (v && idInput) idInput.value = v.id;
      if (v && priceWrap) {
        var html = '<span class="price' + (v.compare ? ' price--sale' : '') + '">' +
          '<span class="price__current">' + v.price + '</span>' +
          (v.compare ? '<s class="price__compare">' + v.compare + '</s>' : '') +
          '</span>';
        priceWrap.innerHTML = html;
      }
      if (v) priceMirrors.forEach(function (el) { el.textContent = v.price; });
      if (addButton) addButton.disabled = !available;
      if (addText) addText.textContent = available ? 'Add to box' : 'Sold out';
      if (v) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url);
        } catch (err) { /* noop */ }
      }
      applyBuilderGate(available);
    }

    // A choice is sold out relative to the OTHER selections, so picking Dozen greys out any
    // shell that has no Dozen variant left. The checked radio stays enabled so a customer is
    // never stranded with nothing selected; the add button reports the sold-out state instead.
    function refreshOptionStates() {
      if (!variantData) return;
      var current = selectedOptionValues();
      root.querySelectorAll('input[type="radio"][data-option-input]').forEach(function (radio) {
        var test = current.slice();
        test[Number(radio.dataset.optionIndex)] = radio.value;
        var v = findVariant(test);
        var soldOut = !v || !v.available;
        var choice = radio.closest('.variant-choice');
        if (choice) choice.classList.toggle('is-soldout', soldOut);
        radio.disabled = soldOut && !radio.checked;
      });
      root.querySelectorAll('[data-option-group]').forEach(function (group) {
        var checked = group.querySelector('input:checked');
        var label = group.querySelector('[data-option-label]');
        if (checked && label) label.textContent = checked.value;
      });
    }

    function onOptionChange() {
      refreshOptionStates();
      applyVariant(findVariant(selectedOptionValues()));
    }

    root.querySelectorAll('[data-option-input]').forEach(function (el) {
      el.addEventListener('change', onOptionChange);
    });
    if (variantData) onOptionChange();

    // AJAX add to cart on the main form
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var qtyInput = form.querySelector('input[name="quantity"]');
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        if (addButton) { addButton.disabled = true; }
        if (addText) { addText.textContent = 'Adding…'; }
        addToCart(idInput.value, qty, collectProperties(form))
          .then(added)
          .catch(function (err) {
            if (err && err.isApiError) { showToast(err.message); }
            else if (!document.hidden) { form.submit(); }
          })
          .finally(function () {
            // Re-derive the button from the selection rather than forcing it enabled: a sold-out
            // combination or a half-filled Party Pack must stay blocked after an add.
            if (variantData) { onOptionChange(); return; }
            if (addButton) addButton.disabled = false;
            if (addText) addText.textContent = 'Add to box';
            applyBuilderGate(true);
          });
      });
    }

    // Buy it now — add then straight to checkout
    initPersonalize(root);

    refreshBuilder = initBoxBuilder(root);
    if (refreshBuilder) {
      root.addEventListener('builder:change', function () { onOptionChange(); });
      onOptionChange();
    }
  }

  /* ---------- Party Pack box builder ---------- */
  // Slots are half dozens: a "Small (24 treats)" box is 4 slots. The size lives in the product's
  // first option, so the target is read from whichever size radio is checked. Add-to-cart stays
  // disabled until the box is exactly full, because a half-filled box is not a product we sell.
  function initBoxBuilder(root) {
    var builder = root.querySelector('[data-box-builder]');
    if (!builder) return null;
    var rows = Array.prototype.slice.call(builder.querySelectorAll('[data-builder-row]'));
    var countEl = builder.querySelector('[data-builder-count]');
    var mixField = builder.querySelector('[data-builder-mix]');
    var detailsEl = builder.querySelector('[data-builder-details]');
    var TREATS_PER_SLOT = 6;

    function slotTarget() {
      var size = root.querySelector('input[data-option-index="0"]:checked');
      if (!size) return 0;
      var treats = parseInt((size.value.match(/\d+/) || [0])[0], 10);
      return Math.floor(treats / TREATS_PER_SLOT);
    }

    function filled() {
      return rows.reduce(function (sum, row) {
        return sum + (parseInt(row.querySelector('[data-builder-n]').textContent, 10) || 0);
      }, 0);
    }

    // Returns whether the box is exactly full, so the caller can gate add-to-cart on it.
    function refresh() {
      var target = slotTarget();
      var used = filled();

      // Switching to a smaller box would otherwise leave more slots allocated than the box
      // holds, with the counter cheerfully reporting "full". Clear it and say so.
      var cleared = false;
      if (target > 0 && used > target) {
        rows.forEach(function (row) { row.querySelector('[data-builder-n]').textContent = '0'; });
        used = 0;
        cleared = true;
      }

      var full = target > 0 && used === target;

      rows.forEach(function (row) {
        var n = parseInt(row.querySelector('[data-builder-n]').textContent, 10) || 0;
        row.querySelector('[data-builder-plus]').disabled = used >= target;
        row.querySelector('[data-builder-minus]').disabled = n === 0;
        row.classList.toggle('is-chosen', n > 0);
      });

      if (target === 0) {
        countEl.textContent = 'Pick a size first';
      } else if (cleared) {
        countEl.textContent = 'Smaller box, so your mix was cleared. Pick ' + target + ' again.';
      } else if (used < target) {
        countEl.textContent = used + ' of ' + target + ' slots filled, ' + (target - used) + ' to go';
      } else {
        countEl.textContent = 'Box full: ' + target + ' of ' + target + ' slots';
      }
      countEl.classList.toggle('is-full', full);
      countEl.classList.toggle('is-cleared', cleared);

      var summary = [];
      detailsEl.innerHTML = '';
      rows.forEach(function (row) {
        var n = parseInt(row.querySelector('[data-builder-n]').textContent, 10) || 0;
        if (!n) return;
        var treat = row.dataset.treat;
        summary.push(n + '× ' + treat);
        var choices = Array.prototype.slice.call(row.querySelectorAll('[data-builder-choice]'))
          .map(function (sel) { return sel.value; });
        // One property per treat keeps the summary short enough for Shopify's property limit
        // and gives the kitchen a line it can read straight off the order.
        var field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'properties[Mix: ' + treat + ']';
        field.value = n + (n === 1 ? ' half dozen' : ' half dozens') +
          (choices.length ? ' · ' + choices.join(' · ') : '');
        detailsEl.appendChild(field);
      });
      mixField.value = summary.join(' · ');
      // An empty name is skipped by collectProperties, so an unfilled box adds no stray property.
      mixField.name = summary.length ? 'properties[Mix]' : '';

      return full;
    }

    builder.addEventListener('click', function (e) {
      var plus = e.target.closest('[data-builder-plus]');
      var minus = e.target.closest('[data-builder-minus]');
      if (!plus && !minus) return;
      var row = (plus || minus).closest('[data-builder-row]');
      var nEl = row.querySelector('[data-builder-n]');
      var n = parseInt(nEl.textContent, 10) || 0;
      nEl.textContent = plus ? n + 1 : Math.max(0, n - 1);
      builder.dispatchEvent(new CustomEvent('builder:change', { bubbles: true }));
    });
    builder.addEventListener('change', function (e) {
      if (e.target.closest('[data-builder-choice]')) {
        builder.dispatchEvent(new CustomEvent('builder:change', { bubbles: true }));
      }
    });

    return refresh;
  }

  /* ---------- "Make it yours" panel ---------- */
  // Presets and the free-text colour field both post as properties[Colors]; this keeps exactly
  // one of them filled so the line never carries two colour values, and echoes the choice in
  // each legend. Counters read maxlength so the limit lives in the markup only.
  function initPersonalize(page) {
    var panel = page.querySelector('[data-personalize]');
    if (!panel) return;
    var otherToggle = panel.querySelector('[data-palette-other-toggle]');
    var otherWrap = panel.querySelector('[data-palette-other]');
    var otherText = panel.querySelector('[data-palette-text]');
    var occasionLabel = panel.querySelector('[data-personalize-label="occasion"]');
    var colorsLabel = panel.querySelector('[data-personalize-label="colors"]');

    function updateLabels() {
      var occasion = panel.querySelector('[data-personalize-radio="occasion"]:checked');
      if (occasionLabel) occasionLabel.textContent = (occasion && occasion.value) || 'No occasion';
      var preset = panel.querySelector('[data-personalize-radio="colors"]:checked');
      if (colorsLabel) {
        if (preset) colorsLabel.textContent = preset.value;
        else if (otherToggle && otherToggle.checked) colorsLabel.textContent = otherText.value.trim() || 'Other';
        else colorsLabel.textContent = "Baker's choice";
      }
    }

    function updateCounter(field) {
      var label = field.parentElement.querySelector('[data-counter-label]');
      var max = parseInt(field.getAttribute('maxlength'), 10);
      if (label && max) label.textContent = (max - field.value.length) + ' left';
    }

    panel.addEventListener('change', function (e) {
      var radio = e.target.closest('input[type="radio"]');
      if (!radio) return;
      if (radio === otherToggle) {
        panel.querySelectorAll('[data-personalize-radio="colors"]').forEach(function (r) { r.checked = false; });
        otherWrap.hidden = false;
        otherText.focus();
      } else if (radio.dataset.personalizeRadio === 'colors' && otherToggle) {
        otherToggle.checked = false;
        otherWrap.hidden = true;
        otherText.value = '';
        updateCounter(otherText);
      }
      updateLabels();
    });
    panel.addEventListener('input', function (e) {
      var field = e.target.closest('[data-counter]');
      if (field) updateCounter(field);
      if (field === otherText) updateLabels();
    });
    updateLabels();
  }

  function escapeHtml(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ---------- Share (Web Share API + fallbacks) ---------- */
  function initShare() {
    document.documentElement.classList.toggle('supports-webshare', !!navigator.share);
    document.addEventListener('click', function (e) {
      var native = e.target.closest('[data-share-native]');
      if (native) {
        e.preventDefault();
        navigator.share({ title: native.dataset.title || document.title, url: native.dataset.url || window.location.href }).catch(function () {});
        return;
      }
      var copy = e.target.closest('[data-share-copy]');
      if (copy) {
        e.preventDefault();
        var url = copy.dataset.url || window.location.href;
        var label = copy.querySelector('[data-share-copy-label]');
        var done = function () {
          if (!label) return;
          var original = label.textContent;
          label.textContent = 'Copied!';
          setTimeout(function () { label.textContent = original; }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done).catch(done);
        } else {
          var tmp = document.createElement('input');
          tmp.value = url; document.body.appendChild(tmp); tmp.select();
          try { document.execCommand('copy'); } catch (err) { /* noop */ }
          document.body.removeChild(tmp); done();
        }
      }
    });
  }

  /* ---------- Newsletter popup ---------- */
  function initNewsletterPopup() {
    var popup = document.getElementById('NewsletterPopup');
    var s = window.themeSettings || {};
    if (!popup || !s.popupEnabled) return;

    var STORE_KEY = 'pcb_popup_dismissed';
    function dismissedRecently() {
      try {
        var ts = parseInt(localStorage.getItem(STORE_KEY), 10);
        return ts && (Date.now() - ts) < 1000 * 60 * 60 * 24 * 30; // 30 days
      } catch (e) { return false; }
    }
    function markDismissed() {
      try { localStorage.setItem(STORE_KEY, String(Date.now())); } catch (e) { /* noop */ }
    }

    // If the form just posted successfully, show it once so the thank-you is visible.
    var justSucceeded = !!popup.querySelector('.form-success');
    if (dismissedRecently() && !justSucceeded) return;

    function close() {
      popup.hidden = true;
      document.body.style.overflow = '';
      markDismissed();
    }
    popup.addEventListener('click', function (e) {
      if (e.target === popup || e.target.closest('[data-popup-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !popup.hidden) close();
    });

    function show() {
      if (!popup.hidden) return;
      popup.hidden = false;
      var focusable = popup.querySelector('input, button');
      if (focusable && !justSucceeded) setTimeout(function () { focusable.focus(); }, 40);
    }

    if (justSucceeded) { show(); return; }
    setTimeout(show, Math.max(1, (s.popupDelay || 12)) * 1000);
  }

  /* ---------- Live search overlay ---------- */
  function initSearchOverlay() {
    var overlay = document.getElementById('SearchOverlay');
    if (!overlay) return;
    var input = overlay.querySelector('[data-search-input]');
    var results = overlay.querySelector('[data-search-results]');
    var debounceTimer = null;
    var lastQuery = '';

    function close() {
      overlay.hidden = true;
      document.body.style.overflow = '';
    }
    function open() {
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      setTimeout(function () { input.focus(); }, 30);
    }
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.closest('[data-search-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hidden) close();
    });

    function render(data, query) {
      var products = (data.resources && data.resources.results && data.resources.results.products) || [];
      if (!products.length) {
        results.innerHTML = '<p class="search-overlay__empty">No treats found for "' + escapeHtml(query) + '". <a href="/collections/sweet-treats">Browse everything</a></p>';
        return;
      }
      results.innerHTML = products.map(function (p) {
        var img = p.image ? p.image + (p.image.indexOf('?') > -1 ? '&' : '?') + 'width=128' : '';
        var cents = Math.round(parseFloat(p.price) * 100);
        return '<a class="search-overlay__item" href="' + p.url.split('?')[0] + '" data-quickview>' +
          (img ? '<img src="' + img + '" alt="" width="56" height="56" loading="lazy">' : '') +
          '<span class="search-overlay__item-title">' + escapeHtml(p.title) + '</span>' +
          (isNaN(cents) ? '' : '<span class="search-overlay__item-price">' + formatMoney(cents) + '</span>') +
          '</a>';
      }).join('') +
      '<a class="search-overlay__all" href="' + ((window.themeSettings && window.themeSettings.rootUrl) || '/') + 'search?q=' + encodeURIComponent(query) + '">See all results for "' + escapeHtml(query) + '"</a>';
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounceTimer);
      if (q.length < 2) { results.innerHTML = ''; lastQuery = ''; return; }
      debounceTimer = setTimeout(function () {
        lastQuery = q;
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last')
          .then(function (r) { if (!r.ok) throw new Error('suggest failed'); return r.json(); })
          .then(function (data) { if (q === lastQuery) render(data, q); })
          .catch(function () { results.innerHTML = ''; });
      }, 200);
    });

    document.addEventListener('click', function (e) {
      var a = e.target.closest('a.site-header__icon[aria-label="Search"]');
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      open();
    });
  }

  /* ---------- Cart drawer ---------- */
  function initCartDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    var body = drawer.querySelector('[data-cart-drawer-body]');
    var foot = drawer.querySelector('[data-cart-drawer-foot]');
    var summary = drawer.querySelector('[data-cart-drawer-summary]');
    var totalEl = drawer.querySelector('[data-cart-drawer-total]');
    var shipEl = drawer.querySelector('[data-cart-drawer-ship]');
    var subEl = drawer.querySelector('[data-cart-drawer-subtotal]');
    var upsellEl = drawer.querySelector('[data-cart-drawer-upsell]');
    var extras = drawer.querySelector('[data-cart-drawer-extras]');
    var upsellProduct = null;
    var upsellLoaded = false;

    initCartExtras(drawer);

    var autoOpened = false;
    function close() {
      drawer.hidden = true;
      document.body.style.overflow = '';
      // A drawer that opened because the customer landed on /cart closes back to where they
      // came from on this site, rather than leaving them on the /cart address.
      if (autoOpened) {
        autoOpened = false;
        var fromHere = document.referrer && document.referrer.indexOf(window.location.origin + '/') === 0;
        if (fromHere && window.history.length > 1) { window.history.back(); } else { leaveAutoAddress(); }
      }
    }
    drawer.addEventListener('click', function (e) {
      if (e.target === drawer || e.target.closest('[data-cart-drawer-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });

    function renderShip(cart) {
      var s = window.themeSettings || {};
      if (!s.showFreeShipping || !s.freeShippingThreshold) { shipEl.innerHTML = ''; return; }
      var thresholdCents = s.freeShippingThreshold * 100;
      var remaining = thresholdCents - cart.items_subtotal_price;
      var progress = Math.min(100, Math.round(cart.items_subtotal_price * 100 / thresholdCents));
      shipEl.innerHTML = '<div class="shipping-bar shipping-bar--drawer">' +
        (remaining > 0
          ? '<p class="shipping-bar__text">You\'re <strong>' + formatMoney(remaining) + '</strong> away from free local delivery</p>'
          : '<p class="shipping-bar__text shipping-bar__text--done">You\'ve unlocked free local delivery!</p>') +
        '<div class="shipping-bar__track"><div class="shipping-bar__fill" style="width: ' + progress + '%"></div></div>' +
        '</div>';
    }

    function renderUpsell(cart) {
      if (!upsellEl) return;
      var handle = (window.themeSettings || {}).upsellHandle;
      if (!handle || !upsellProduct) { upsellEl.hidden = true; return; }
      var already = cart.items.some(function (it) { return it.handle === handle; });
      var variant = (upsellProduct.variants || []).filter(function (v) { return v.available; })[0];
      if (already || !variant) { upsellEl.hidden = true; return; }
      var img = upsellProduct.featured_image ? upsellProduct.featured_image + (upsellProduct.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=96' : '';
      upsellEl.innerHTML = '<p class="cart-drawer__upsell-label">Add to your box</p>' +
        '<div class="cart-drawer__upsell">' +
          (img ? '<img src="' + img + '" alt="" width="44" height="44" loading="lazy">' : '') +
          '<span class="cart-drawer__upsell-title">' + escapeHtml(upsellProduct.title) + '</span>' +
          '<button type="button" class="button button--small" data-upsell-add="' + variant.id + '">Add ' + formatMoney(variant.price) + '</button>' +
        '</div>';
      upsellEl.hidden = false;
    }

    function ensureUpsell(cart) {
      var handle = (window.themeSettings || {}).upsellHandle;
      if (!handle || !upsellEl) { if (upsellEl) upsellEl.hidden = true; return; }
      if (upsellLoaded) { renderUpsell(cart); return; }
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { upsellLoaded = true; upsellProduct = p; renderUpsell(cart); })
        .catch(function () { upsellLoaded = true; });
    }

    function render(cart) {
      syncCartChrome(cart);
      if (!cart.items.length) {
        var shopUrl = (window.themeSettings && window.themeSettings.rootUrl) || '/';
        body.innerHTML = '<div class="cart-drawer__empty">' +
          '<p class="cart-drawer__empty-title">Nothing in the box yet</p>' +
          '<p>Pick something from the case to get started.</p>' +
          '<a class="button" href="' + shopUrl + '">Browse the case</a></div>';
        foot.hidden = true;
        if (summary) summary.hidden = true;
        if (extras) extras.hidden = true;
        return;
      }
      if (extras) extras.hidden = false;
      if (summary) summary.hidden = false;
      body.innerHTML = cart.items.map(function (item, i) {
        var img = item.image ? item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=128' : '';
        var line = i + 1;
        return '<div class="cart-drawer__line">' +
          (img ? '<img src="' + img + '" alt="" width="56" height="56" loading="lazy">' : '<span class="cart-drawer__line-ph"></span>') +
          '<div class="cart-drawer__line-info">' +
            '<div class="cart-drawer__line-top">' +
              '<p class="cart-drawer__line-title">' + escapeHtml(item.product_title) + '</p>' +
              '<p class="cart-drawer__line-price">' + formatMoney(item.final_line_price) + '</p>' +
            '</div>' +
            (item.variant_title && item.variant_title !== 'Default Title'
              ? '<p class="cart-drawer__line-variant">' + escapeHtml(item.variant_title) + '</p>' : '') +
            linePropertiesHtml(item.properties) +
            '<div class="cart-drawer__line-actions">' +
              '<div class="qty qty--pill">' +
                '<button type="button" class="qty__btn" data-drawer-qty="' + (item.quantity - 1) + '" data-line="' + line + '" aria-label="Decrease quantity">&minus;</button>' +
                '<span class="cart-drawer__qty">' + item.quantity + '</span>' +
                '<button type="button" class="qty__btn" data-drawer-qty="' + (item.quantity + 1) + '" data-line="' + line + '" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="button" class="cart-line__remove" data-drawer-qty="0" data-line="' + line + '" aria-label="Remove ' + escapeHtml(item.product_title) + '">Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      }).join('');
      subEl.textContent = formatMoney(cart.items_subtotal_price);
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
      renderShip(cart);
      ensureUpsell(cart);
      foot.hidden = false;
    }

    body.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-drawer-qty]');
      if (!btn) return;
      var quantity = Math.max(0, parseInt(btn.dataset.drawerQty, 10) || 0);
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: Number(btn.dataset.line), quantity: quantity })
      }).then(function (r) {
        if (!r.ok) throw new Error('change failed');
        return r.json();
      }).then(render)
        .catch(function () { if (document.hidden) { close(); } else { window.location.href = (window.themeSettings && window.themeSettings.cartUrl) || '/cart'; } });
    });

    if (upsellEl) {
      upsellEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-upsell-add]');
        if (!btn) return;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        addToCart(btn.dataset.upsellAdd, 1)
          .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(render)
          .catch(function () { if (document.hidden) { close(); } else { window.location.href = (window.themeSettings && window.themeSettings.cartUrl) || '/cart'; } });
      });
    }

    function open(auto) {
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      body.innerHTML = '<p class="cart-drawer__empty">Loading&hellip;</p>';
      foot.hidden = true;
      fetch('/cart.js')
        .then(function (r) { if (!r.ok) throw new Error('unavailable'); return r.json(); })
        .then(render)
        .catch(function () {
          // The fallback is to load /cart, which on /cart is this page: close instead, or a
          // failed fetch would reload forever.
          if (auto === true || document.hidden) { close(); return; }
          window.location.href = (window.themeSettings && window.themeSettings.cartUrl) || '/cart';
        });
    }
    openCartDrawer = open;

    if (window.themeSettings && window.themeSettings.autoCartDrawer && !arrivedByHistory()) {
      open(true);
      autoOpened = true;
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-open-cart]')) { open(); return; }
      var a = e.target.closest('a.site-header__cart, a.cart-toast__link');
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      open();
    });
  }

  // Mirrors the cart page: every non-empty property except underscore-prefixed ones.
  function linePropertiesHtml(properties) {
    var p = properties || {};
    return Object.keys(p).filter(function (k) { return p[k] && k.charAt(0) !== '_'; }).map(function (k) {
      return '<p class="cart-drawer__line-variant">' + escapeHtml(k) + ': ' + escapeHtml(p[k]) + '</p>';
    }).join('');
  }

  // trimZeros drops ".00" for chrome labels like the header pill, where a whole-dollar
  // total reads better short. Line prices and totals keep the cents.
  function formatMoney(cents, trimZeros) {
    var amount = (cents / 100).toFixed(2);
    if (trimZeros && amount.slice(-3) === '.00') amount = amount.slice(0, -3);
    var fmt = (window.themeSettings && window.themeSettings.moneyFormat) || '${{amount}}';
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  /* ---------- Cart extras: needed-by date, gift note, order note ---------- */
  // These write straight to the cart as the customer touches them, so nothing is lost by
  // closing the drawer and there is no "update" button to forget.
  function saveCart(payload) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart update failed');
      return r.json();
    });
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  function initCartExtras(root) {
    var strip = root.querySelector('[data-needed-by]');
    if (strip) {
      var days = Array.prototype.slice.call(strip.querySelectorAll('[data-needed-by-day]'));
      var native = strip.querySelector('[data-needed-by-native]');

      function selectDay(iso) {
        days.forEach(function (btn) {
          var on = btn.dataset.neededByDay === iso;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (native) native.value = iso;
        strip.dataset.selected = iso;
        var stale = strip.querySelector('[data-needed-by-stale]');
        if (stale) stale.hidden = true;
        saveCart({ attributes: { 'Needed by': iso } }).catch(function () {
          showToast('That date did not save. Try again.');
        });
      }

      days.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          selectDay(btn.dataset.neededByDay);
        });
      });
      if (native) {
        native.addEventListener('change', function () { selectDay(native.value); });
      }

      // A date chosen days ago can fall behind the lead time before checkout; drop it so the
      // kitchen is never handed a date it cannot make.
      var chosen = strip.dataset.selected;
      if (chosen && chosen < strip.dataset.earliest) selectDay('');
    }

    var giftToggle = root.querySelector('[data-gift-toggle]');
    var giftNote = root.querySelector('[data-gift-note]');
    if (giftToggle && giftNote) {
      giftToggle.addEventListener('click', function () {
        var on = giftToggle.getAttribute('aria-pressed') !== 'true';
        giftToggle.setAttribute('aria-pressed', on ? 'true' : 'false');
        giftNote.hidden = !on;
        if (on) { giftNote.focus(); return; }
        giftNote.value = '';
        saveCart({ attributes: { 'Gift message': '' } }).catch(function () {});
      });
      giftNote.addEventListener('input', debounce(function () {
        saveCart({ attributes: { 'Gift message': giftNote.value } }).catch(function () {});
      }, 500));
    }

    var note = root.querySelector('[data-cart-note]');
    if (note) {
      note.addEventListener('input', debounce(function () {
        saveCart({ note: note.value }).catch(function () {});
      }, 500));
    }
  }

  /* ---------- "The case" category chips and search ---------- */
  // Every product is already in the DOM, so switching category or typing filters the rendered
  // cards instead of loading a page. The heading and count follow the visible set.
  function initProductCase() {
    var root = document.querySelector('[data-product-case]');
    if (!root) return;
    var grid = root.querySelector('[data-case-grid]');
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-case-chip]'));
    var search = root.querySelector('[data-case-search]');
    var title = root.querySelector('[data-case-title]');
    var count = root.querySelector('[data-case-count]');
    var empty = root.querySelector('[data-case-empty]');
    var reset = root.querySelector('[data-case-reset]');
    var items = Array.prototype.slice.call(grid.children);
    var baseTitle = title ? title.textContent : '';
    var handle = '';

    function apply() {
      var term = search ? search.value.trim().toLowerCase() : '';
      var shown = 0;
      items.forEach(function (li) {
        var card = li.querySelector('[data-product-card]');
        if (!card) return;
        var inCategory = !handle || (' ' + card.dataset.collections + ' ').indexOf(' ' + handle + ' ') !== -1;
        var matches = !term ||
          card.dataset.title.indexOf(term) !== -1 ||
          card.dataset.type.indexOf(term) !== -1;
        var show = inCategory && matches;
        li.hidden = !show;
        if (show) shown += 1;
      });
      if (count) count.textContent = shown + (shown === 1 ? ' treat' : ' treats');
      if (empty) empty.hidden = shown !== 0;
      if (title) {
        var chip = chips.filter(function (c) { return c.dataset.caseChip === handle; })[0];
        title.textContent = handle && chip ? chip.textContent.trim() : baseTitle;
      }
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        handle = chip.dataset.caseChip;
        chips.forEach(function (c) {
          var on = c === chip;
          c.classList.toggle('is-selected', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        apply();
      });
    });
    if (search) search.addEventListener('input', apply);
    if (reset) {
      reset.addEventListener('click', function () {
        handle = '';
        if (search) search.value = '';
        chips.forEach(function (c, i) {
          c.classList.toggle('is-selected', i === 0);
          c.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
        });
        apply();
      });
    }
  }

  /* ---------- Quick view panel ---------- */
  // The panel is the quick-view section rendered by Shopify, so it shows the same option
  // controls, personalisation and pricing as the product page with no parallel renderer here.
  function initQuickView() {
    var modal = document.getElementById('QuickView');
    if (!modal) return;
    var content = modal.querySelector('[data-quickview-content]');
    var markup = {};

    var autoOpened = false;
    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      // A panel that opened because the customer followed a product link from our own grid
      // should close back to that grid, not strand them on /products/<handle>.
      if (autoOpened) {
        autoOpened = false;
        var fromHere = document.referrer && document.referrer.indexOf(window.location.origin + '/') === 0;
        if (fromHere && window.history.length > 1) { window.history.back(); } else { leaveAutoAddress(); }
      }
    }
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target.closest('[data-quickview-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });

    // Adding leaves the panel open so a second box can be configured. The confirmation shows
    // inside the panel, because the page toast sits beneath this overlay.
    function panelAdded() {
      updateCartCount();
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.classList.remove('cart-bump');
        void el.offsetWidth;
        el.classList.add('cart-bump');
      });
      var note = content.querySelector('[data-panel-added]');
      if (note) { note.hidden = false; } else { showToast('✓ Added to your box'); }
    }

    function show(html) {
      content.innerHTML = html;
      bindProductForm(content, panelAdded);
      var focusTarget = content.querySelector('input, button');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    function openFor(url, auto) {
      var clean = url.split('#')[0].split('?')[0];
      var searchOverlay = document.getElementById('SearchOverlay');
      if (searchOverlay) searchOverlay.hidden = true;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      content.innerHTML = '<p class="quickview__loading">Loading&hellip;</p>';
      if (markup[clean]) { show(markup[clean]); return; }
      fetch(clean + '?section_id=quick-view')
        .then(function (r) { if (!r.ok) throw new Error('unavailable'); return r.text(); })
        .then(function (html) { markup[clean] = html; show(html); })
        .catch(function () {
          // The fallback is to navigate to the product URL — but on a product URL that IS this
          // page, so navigating would reload forever. Close instead.
          if (auto || document.hidden) { close(); return; }
          window.location.href = clean;
        });
    }

    // On a product URL the template already rendered this product's panel inline, with the
    // real Judge.me review block in it. Move that node into the modal rather than fetching a
    // copy: Judge.me only initialises review blocks present at page load, and a fetched copy
    // would also duplicate every id in the form.
    var auto = window.themeSettings && window.themeSettings.autoQuickView;
    if (auto && !arrivedByHistory()) {
      var inline = document.querySelector('[data-inline-quickview] [data-quickview-product]');
      if (inline) {
        var searchOverlay = document.getElementById('SearchOverlay');
        if (searchOverlay) searchOverlay.hidden = true;
        content.innerHTML = '';
        content.appendChild(inline);
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        bindProductForm(content, panelAdded);
        autoOpened = true;
      } else {
        openFor(auto, true);
      }
    }

    // Card and search-result links are NOT intercepted any more: they navigate to the product
    // URL, which renders the grid and opens this product's panel on load. That costs a page
    // load (initPrefetch warms it on hover/touch), but it is the only way Judge.me's review
    // block initialises, because Judge.me scans for it once at load and never again.
  }

  /* ---------- Hover/touch prefetch of internal pages ---------- */
  function initPrefetch() {
    var seen = {};
    function prefetch(url) {
      if (seen[url]) return;
      seen[url] = true;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      document.head.appendChild(link);
    }
    function handler(e) {
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) !== '/' || href.indexOf('//') === 0) return;
      if (href.indexOf('/cart') === 0 || href.indexOf('/checkout') === 0 || href.indexOf('/account') === 0) return;
      prefetch(href.split('#')[0]);
    }
    document.addEventListener('mouseover', handler);
    document.addEventListener('touchstart', handler, { passive: true });
  }

  /* ---------- Cart page ---------- */
  /* ---------- Collection sorting & filters ---------- */
  /* ---------- Country/province selects on address form ---------- */
  function initAddressForm() {
    var country = document.querySelector('[data-address-country]');
    var province = document.querySelector('[data-address-province]');
    if (!country || !province) return;

    function updateProvinces() {
      var selected = country.options[country.selectedIndex];
      var provincesAttr = selected ? selected.getAttribute('data-provinces') : null;
      province.innerHTML = '';
      var provinces = [];
      try { provinces = JSON.parse(provincesAttr || '[]'); } catch (e) { provinces = []; }
      var wrapper = province.closest('label') || province;
      if (!provinces.length) {
        wrapper.style.display = 'none';
        return;
      }
      wrapper.style.display = '';
      provinces.forEach(function (p) {
        var opt = document.createElement('option');
        opt.value = p[0];
        opt.textContent = p[1];
        if (p[0] === province.dataset.default) opt.selected = true;
        province.appendChild(opt);
      });
    }

    if (country.dataset.default) {
      for (var i = 0; i < country.options.length; i++) {
        if (country.options[i].value === country.dataset.default) { country.selectedIndex = i; break; }
      }
    }
    country.addEventListener('change', updateProvinces);
    updateProvinces();
  }

  /* Mobile browser chrome ----------------------------------------------------
     Some mobile browsers (iOS Safari, and Chrome when its toolbar sits at the bottom) draw
     their own bar OVER the layout viewport rather than shrinking it, so a `bottom: 0` element
     ends up underneath it. The visual viewport reports that overlap; publish it as a custom
     property and the CSS lifts the tab bar, the chat launcher and the toast clear of it. */
  function initViewportChrome() {
    var vv = window.visualViewport;
    if (!vv) return;
    var root = document.documentElement;
    var queued = false;

    function measure() {
      queued = false;
      var gap = window.innerHeight - vv.height - vv.offsetTop;
      // An open keyboard produces the same gap but is not browser chrome, and lifting the bar
      // by the keyboard's height would strand it mid-screen. Anything that big is not a toolbar.
      if (!isFinite(gap) || gap < 1 || gap > 140) gap = 0;
      root.style.setProperty('--browser-chrome', Math.round(gap) + 'px');
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(measure);
    }

    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('orientationchange', schedule);
    measure();
  }

  /* Chip radio groups ----------------------------------------------------------
     Chips and day tiles on forms are radio inputs inside labels. CSS :has(input:checked)
     draws the selection; this mirrors it onto .is-selected for browsers without :has(). */
  function initChipGroups() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-chip-group]'), function (group) {
      function sync() {
        Array.prototype.forEach.call(group.querySelectorAll('input[type="radio"]'), function (input) {
          var label = input.closest('label');
          if (label) label.classList.toggle('is-selected', input.checked);
        });
      }
      group.addEventListener('change', sync);
      sync();
    });
  }

  /* Order again --------------------------------------------------------------
     Each Reorder button carries the variant, quantity and the line's personalisation as JSON,
     so a reordered Party Pack comes back with the same mix. */
  function initReorder() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-reorder]');
      if (!btn || btn.disabled) return;
      var props = null;
      try { props = JSON.parse(btn.dataset.properties || '{}'); } catch (err) { props = null; }
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      addToCart(btn.dataset.variantId, parseInt(btn.dataset.quantity, 10) || 1, props)
        .then(function () {
          btn.textContent = 'In box ✓';
          btn.classList.add('is-done');
          addedFeedback();
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = label;
          showToast(err && err.isApiError ? err.message : 'That did not add. Try again.');
        });
    });
  }

  onReady(function () {
    initMenu();
    initQty();
    initQuickAdd();
    initAddressForm();
    initPrefetch();
    initQuickView();
    initCartDrawer();
    initSearchOverlay();
    initNewsletterPopup();
    initShare();
    initProductCase();
    initViewportChrome();
    initChipGroups();
    initReorder();
  });

  // Safety net: if the page is restored (bfcache) or refocused with no overlay
  // open, make sure the body scroll lock hasn't been left on.
  window.addEventListener('pageshow', function () {
    var anyOpen = ['QuickView', 'CartDrawer', 'SearchOverlay', 'NewsletterPopup'].some(function (id) {
      var el = document.getElementById(id);
      return el && !el.hidden;
    });
    if (!anyOpen) document.body.style.overflow = '';
  });
})();
